import type { Entry, FilterItem } from '../../features/admin/modules/modules.model';
import type { ProminenceCondition, ProminenceLevel } from '../../features/admin/widgets/widgets.model';
import type { ConditionDateCastFields, DateExtract } from '../types/condition-cast.types';
import { DateHelperService } from './date-helper.service';
import { UtilityService } from './utility.service';

/**
 * Cache key of an external-entries question. Deliberately `(moduleCode, filter)` and nothing else:
 * two conditions asking the same question cost one request, whichever surface they sit on.
 *
 * Lives here rather than next to ExternalEntriesEvaluatorService so this dependency-free util does
 * not have to import the injectable (which would pull ModulesStore into every consumer).
 */
export function externalEntriesKey(moduleCode: string, filter: string): string {
  return `${moduleCode}||${filter}`;
}

/**
 * A resolved external-entries answer.
 *
 * `count` is capped at the `$top` the request was issued with, so it is NOT a plain total: a
 * `{ count: 1, top: 1 }` means "at least 1", not "exactly 1". `top` therefore has to travel with
 * the count — without it a cheap pass (top 1) would answer a later `minCount: 5` question with a
 * confident, wrong "no".
 */
export interface ExternalEntriesCount {
  count: number;
  top: number;
}

/**
 * Is the recorded answer enough for `minCount`?
 *
 * Three cases:
 * - nothing recorded yet → false, so a condition never flashes true before its answer lands
 * - the count came back below its cap → it is the real total, compare directly
 * - the count hit its cap → we only know "at least `top`", which answers `minCount <= top` and
 *   leaves anything beyond that genuinely unknown, so it reads false until a wider pass runs
 */
export function isExternalEntriesSatisfied(
  recorded: ExternalEntriesCount | undefined,
  minCount: number | undefined
): boolean {
  if (!recorded) {
    return false;
  }
  const required = Math.max(1, minCount ?? 1);
  if (recorded.count < recorded.top) {
    return recorded.count >= required;
  }
  return required <= recorded.top;
}

/**
 * Evaluates whether a candidate entry satisfies a set of FilterItem[].
 * Reusable for both planning drag-drop and cascading auto-fill.
 *
 * @param candidateEntry  The entry being evaluated (e.g. a linked employee)
 * @param contextEntry    The current entry (for dynamic filter values)
 * @param filters         FilterItem[] of the target field
 * @param repeatIndex     Array index for repeat fields (optional)
 * @param now             Current date for date-operator evaluation (optional, default = new Date())
 * @param externalCounts  Resolved counts by `externalEntriesKey`, required only when the set
 *                        contains `basedOn: 'externalEntries'` items. This function stays
 *                        synchronous, so those counts must already have been fetched by
 *                        ExternalEntriesEvaluatorService; an absent count reads as not satisfied.
 */
// eslint-disable-next-line max-params
export function evaluateFiltersAgainstEntry(
  candidateEntry: Entry,
  contextEntry: Entry,
  filters: FilterItem[],
  repeatIndex?: number[],
  now: Date = new Date(),
  externalCounts?: Record<string, ExternalEntriesCount>
): boolean {
  return filters.every(filter => {
    if (!filter || Object.keys(filter).length === 0) {
      return true;
    }

    // Standalone truth test: it asks about other entries entirely, so there is no subject value on
    // the candidate to compare against and none of the operator machinery below applies.
    if (filter.basedOn === 'externalEntries') {
      if (!filter.moduleCode) {
        return false;
      }
      const key = externalEntriesKey(filter.moduleCode, filter.filter?.trim() ?? '');
      return isExternalEntriesSatisfied(externalCounts?.[key], filter.minCount);
    }

    const subjectValue = UtilityService.keyPathToValue(candidateEntry, filter.compareField);

    let compareValue: any;
    if (filter.basedOn === 'dynamic') {
      const keyPath = filter.fieldKeyPath + (filter.subFieldKeyPath ? '.' + filter.subFieldKeyPath : '');
      compareValue = UtilityService.keyPathToValue(contextEntry, keyPath, repeatIndex ? [...repeatIndex] : undefined);

      if (compareValue === undefined || compareValue === null) {
        return true;
      }
    } else if (filter.basedOn === 'currentDate') {
      compareValue = DateHelperService.resolveCurrentDateWithOffset(
        now,
        filter.useOffset,
        filter.dateOffset
      );
    } else {
      compareValue = filter.value;
    }

    if (Array.isArray(compareValue) && compareValue.length === 1) {
      compareValue = compareValue[0];
    }

    if (Array.isArray(compareValue) && compareValue.length === 0) {
      return true;
    }

    // Resolve compareValue2 for between operator
    let compareValue2: any = undefined;
    if (filter.operator === 'between') {
      if (filter.basedOn2 === 'currentDate') {
        compareValue2 = DateHelperService.resolveCurrentDateWithOffset(now, filter.useOffset2, filter.dateOffset2);
      } else if (filter.basedOn2 === 'dynamic') {
        // TODO: fieldKeyPath2 / subFieldKeyPath2 are FilterItem-specific (only widget filters; not
        // prominence conditions). When adding `dynamic` basedOn2 support more broadly, add these
        // to ConditionDateCastFields and remove the (filter as any) cast.
        const f = filter as any;
        const kp2 = (f.fieldKeyPath2 ?? '') + (f.subFieldKeyPath2 ? '.' + f.subFieldKeyPath2 : '');
        compareValue2 = kp2 ? UtilityService.keyPathToValue(contextEntry, kp2, repeatIndex ? [...repeatIndex] : undefined) : undefined;
      } else {
        compareValue2 = filter.value2;
      }
    }

    return evaluateFilterCondition(subjectValue, filter.operator, compareValue, now, filter, compareValue2);
  });
}

function evaluateFilterCondition(
  subjectValue: any,
  operator: string,
  compareValue: any,
  now: Date = new Date(),
  condition?: ConditionDateCastFields,
  compareValue2?: any
): boolean {
  // null/empty handling — checked first because isnull semantics ignore extract/compareValue.
  if (operator === 'isnull') {
    return subjectValue === null || subjectValue === undefined || subjectValue === '';
  }
  if (operator === '!isnull') {
    return subjectValue !== null && subjectValue !== undefined && subjectValue !== '';
  }

  if (operator === 'between') {
    return evaluateBetween(subjectValue, compareValue, compareValue2, condition?.extract);
  }

  // Apply extract symmetrically before any compare logic
  const extract = condition?.extract;
  if (extract && extract !== 'none') {
    const subjectExtracted = DateHelperService.extractDateComponent(subjectValue as any, extract);
    const compareExtracted = DateHelperService.extractDateComponent(compareValue as any, extract);
    if (subjectExtracted === null) return operator === '!=' || operator === 'ne';
    return compareNumbers(subjectExtracted, compareExtracted, operator);
  }

  if (subjectValue === null || subjectValue === undefined) {
    return operator === '!=' || operator === 'ne';
  }

  const extractIds = (val: any): string[] => {
    if (Array.isArray(val)) {
      return val.map(v => (typeof v === 'object' && v?.id ? v.id : String(v)));
    }
    if (typeof val === 'object' && val !== null) {
      return [val.id ?? val.selectedInput ?? String(val)];
    }
    return [String(val)];
  };

  const subjectIds = extractIds(subjectValue);
  const compareIds = extractIds(compareValue);

  switch (operator) {
    case '==':
    case 'eq':
    case 'contains':
    case 'includes':
      return compareIds.some(cId => subjectIds.includes(cId));
    case '!=':
    case 'ne':
      return !compareIds.some(cId => subjectIds.includes(cId));
    case '>':
    case 'gt':
    case '<':
    case 'lt':
    case '>=':
    case 'gte':
    case '<=':
    case 'lte': {
      const subjectN = typeof subjectValue === 'number' ? subjectValue : DateHelperService.normalizeForDateComparison(subjectValue);
      const compareN = typeof compareValue === 'number' ? compareValue : DateHelperService.normalizeForDateComparison(compareValue);
      return compareNumbers(subjectN ?? NaN, compareN, operator);
    }
    default:
      return true;
  }
}

function compareNumbers(subject: number, target: number | null, operator: string): boolean {
  if (target === null) return operator === '!=' || operator === 'ne';
  switch (operator) {
    case '==':
    case 'eq':
      return subject === target;
    case '!=':
    case 'ne':
      return subject !== target;
    case '>':
    case 'gt':
      return subject > target;
    case '>=':
    case 'gte':
      return subject >= target;
    case '<':
    case 'lt':
      return subject < target;
    case '<=':
    case 'lte':
      return subject <= target;
    default:
      return false;
  }
}

/** Cyclic extracts wrap at their cycle boundary; inverted bounds (l > u) form a wrap-window. */
const CYCLIC_EXTRACTS: ReadonlySet<DateExtract> = new Set([
  'month-day',
  'month',
  'day-of-week',
  'iso-week',
  'time',
  'hour-of-day',
]);

function evaluateBetween(subject: any, lower: any, upper: any, extract?: DateExtract): boolean {
  // Reuse pad: voor pure Date subjects zonder extract delegeer naar bestaande betweenDates helper.
  if ((!extract || extract === 'none') && subject instanceof Date && lower instanceof Date && upper instanceof Date) {
    if (lower > upper) return false;
    return DateHelperService.betweenDates(lower, upper, subject);
  }
  // Generic numeric/extract path
  const apply = (v: any): number | null => {
    if (extract && extract !== 'none') {
      // Numeric bounds (e.g. hour-of-day between 9 and 17) are already in extracted scale.
      if (typeof v === 'number') return v;
      return DateHelperService.extractDateComponent(v as any, extract);
    }
    return typeof v === 'number' ? v : DateHelperService.normalizeForDateComparison(v);
  };
  const s = apply(subject);
  const l = apply(lower);
  const u = apply(upper);
  if (s === null || l === null || u === null) return false;
  if (l > u) {
    // Cyclic extract (e.g. month-day 1229..0105 across year boundary, hour-of-day 22..02 across midnight)
    // treats inverted bounds as a wrap-around window. Linear extracts and plain numbers stay strict.
    if (extract && CYCLIC_EXTRACTS.has(extract)) return s >= l || s <= u;
    return false;
  }
  return s >= l && s <= u;
}

// `success` ranks LOWEST: it marks a settled, positive state, so an entry that also matches any
// attention level must surface that instead ("expiring soon" beats "valid" on the same document).
const LEVEL_PRIORITY: Record<ProminenceLevel, number> = {
  critical: 4,
  warning: 3,
  info: 2,
  success: 1,
};

/**
 * Evaluates prominence conditions against an entry. Returns the highest-priority
 * matching level, or null if no matches. Priority: critical > warning > info > success.
 *
 * Also returns the CONDITION credited with that win — the first one in array order that matched
 * at the winning level. Callers that need per-condition data (`WidgetItemListComponent` reads
 * `condition.label` for the badge text) must not re-run this function per condition to rediscover
 * it: that turned one evaluation per row into up to one per row PER CONDITION, and a second
 * matching implementation is exactly what this function exists to prevent. `Array.prototype.sort`
 * is stable (ES2019), so equal-priority matches keep their authored order and "first in array
 * order" is precisely what comes out.
 */
export function evaluateProminence(
  candidate: Entry,
  conditions: ProminenceCondition[],
  now: Date = new Date()
): { level: ProminenceLevel; condition: ProminenceCondition } | null {
  if (!conditions?.length) return null;

  const matches: ProminenceCondition[] = [];
  for (const condition of conditions) {
    if (condition.basedOn === 'dynamic') {
      console.warn(
        `[evaluateProminence] basedOn: 'dynamic' is not supported for prominence conditions ` +
        `(no contextEntry available). Condition skipped: compareField=${condition.compareField}`
      );
      continue;
    }

    const subjectValue = UtilityService.keyPathToValue(candidate, condition.compareField);

    let compareValue: any;
    if (condition.basedOn === 'currentDate') {
      compareValue = DateHelperService.resolveCurrentDateWithOffset(
        now,
        condition.useOffset,
        condition.dateOffset
      );
    } else {
      compareValue = condition.value;
    }

    let compareValue2: any = undefined;
    if (condition.operator === 'between') {
      if (condition.basedOn2 === 'currentDate') {
        compareValue2 = DateHelperService.resolveCurrentDateWithOffset(now, condition.useOffset2, condition.dateOffset2);
      } else {
        compareValue2 = condition.value2;
      }
    }

    if (evaluateFilterCondition(subjectValue, condition.operator, compareValue, now, condition, compareValue2)) {
      matches.push(condition);
    }
  }

  if (matches.length === 0) return null;
  // `?? -Infinity` for a condition whose `level` is missing or unknown: `undefined - number` is
  // NaN, and a NaN comparator makes sort's result implementation-defined — it can leave a
  // lower-priority match in front. The portal editor and the MCP schema both always write a level,
  // so this guards hand-authored/legacy JSON rather than a live shape; it also makes an unknown
  // level rank LAST deterministically, which HEAD did not.
  matches.sort((a, b) => (LEVEL_PRIORITY[b.level] ?? -Infinity) - (LEVEL_PRIORITY[a.level] ?? -Infinity));
  return { level: matches[0].level, condition: matches[0] };
}
